import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { booking, intake, payments, workspace } from '@lawbee/domain';
import { Alert, Button, Card, CardContent, Field, FormError, Input, LoadingState, Select, Textarea, ThemeToggle, cn, useToast } from '@lawbee/ui';
import { Check, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage, isApiError } from '../../../lib/api';
import { PROVINCES, TIMEZONES } from '../../../lib/hooks';
import { ME_KEY, useMeQuery } from '../../../lib/me';
import type { AvailabilityRule, Me, Role } from '../../../lib/types';
import { Wordmark } from '../../../shell/Logo';
import { AvailabilityEditor, CopyButton, DEFAULT_RULES } from './shared';

type StepKey = 'firm' | 'practice_areas' | 'availability' | 'payments' | 'team' | 'complete';

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'firm', label: 'Firm' },
  { key: 'practice_areas', label: 'Practice areas' },
  { key: 'availability', label: 'Availability' },
  { key: 'payments', label: 'Payments' },
  { key: 'team', label: 'Team' },
  { key: 'complete', label: 'Done' },
];

type Invitation = { id: string; email: string; role: Role; link: string };

function post(step: StepKey, data: Record<string, unknown>) {
  return api.post<{ ok: boolean; invitations?: Invitation[] }>('/api/workspace/onboarding', { step, data });
}

export function OnboardingPage() {
  const meQ = useMeQuery();
  const navigate = useNavigate();
  const me = meQ.data;

  useEffect(() => {
    if (me && me.role !== 'owner') void navigate({ to: '/dashboard', replace: true });
  }, [me, navigate]);

  if (meQ.isLoading || !me) return <LoadingState label="Loading" className="min-h-dvh" />;
  if (me.role !== 'owner') return <LoadingState label="Opening your dashboard" className="min-h-dvh" />;
  return <Wizard me={me} />;
}

function Wizard({ me }: { me: Me }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [index, setIndex] = useState(() => Math.min(Math.max(me.settings.onboardingStep, 0), STEPS.length - 1));
  const step = STEPS[index] ?? STEPS[0]!;
  const next = () => setIndex((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setIndex((i) => Math.max(i - 1, 0));

  const finish = async () => {
    await post('complete', {});
    await qc.invalidateQueries({ queryKey: ME_KEY });
    await qc.refetchQueries({ queryKey: ME_KEY });
    toast.success('Your workspace is ready');
    await navigate({ to: '/dashboard' });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="flex h-16 items-center justify-between px-4 sm:px-8">
        <Wordmark />
        <ThemeToggle />
      </header>
      <main className="mx-auto w-full max-w-2xl flex-1 px-4 pb-16 pt-4 sm:pt-8">
        <StepIndicator index={index} />
        <Card className="mt-6">
          <CardContent className="p-5 sm:p-8">
            {step.key === 'firm' ? <FirmStep me={me} onDone={next} /> : null}
            {step.key === 'practice_areas' ? <PracticeAreasStep me={me} onDone={next} onBack={back} /> : null}
            {step.key === 'availability' ? <AvailabilityStep onDone={next} onBack={back} /> : null}
            {step.key === 'payments' ? <PaymentsStep onDone={next} onBack={back} /> : null}
            {step.key === 'team' ? <TeamStep onDone={next} onBack={back} /> : null}
            {step.key === 'complete' ? <CompleteStep me={me} onFinish={finish} onBack={back} /> : null}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function StepIndicator({ index }: { index: number }) {
  const current = STEPS[index];
  return (
    <nav aria-label="Setup progress">
      <p className="mb-3 text-sm text-muted-foreground">
        Step <span className="figures">{index + 1}</span> of <span className="figures">{STEPS.length}</span>: <span className="font-medium text-foreground">{current?.label}</span>
      </p>
      <ol className="flex gap-1.5">
        {STEPS.map((s, i) => (
          <li key={s.key} className="flex-1" aria-current={i === index ? 'step' : undefined}>
            <span className={cn('block h-1.5 rounded-full', i < index ? 'bg-primary' : i === index ? 'bg-primary/60' : 'bg-foreground/10')} />
            <span className="sr-only">
              {s.label}
              {i < index ? ' (done)' : i === index ? ' (current)' : ''}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function StepHeader({ title, description }: { title: string; description: ReactNode }) {
  return (
    <div className="mb-6 space-y-1.5">
      <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.025em]">{title}</h1>
      <p className="text-[15px] text-muted-foreground">{description}</p>
    </div>
  );
}

function StepActions({ onBack, onSkip, loading, submitLabel = 'Continue' }: { onBack?: () => void; onSkip?: () => void; loading?: boolean; submitLabel?: string }) {
  return (
    <div className="mt-8 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        {onBack ? (
          <Button type="button" variant="ghost" onClick={onBack} disabled={loading}>
            Back
          </Button>
        ) : null}
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        {onSkip ? (
          <Button type="button" variant="outline" onClick={onSkip} disabled={loading}>
            Skip for now
          </Button>
        ) : null}
        <Button type="submit" loading={loading}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function useStep() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setLoading(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  return { loading, error, setError, run };
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

function FirmStep({ me, onDone }: { me: Me; onDone: () => void }) {
  const [name, setName] = useState(me.workspace.name);
  const [phone, setPhone] = useState(me.settings.phone ?? '');
  const [address, setAddress] = useState(me.settings.address ?? '');
  const [timezone, setTimezone] = useState(me.workspace.timezone || 'America/Toronto');
  const [region, setRegion] = useState(me.workspace.region ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  const { loading, error, run } = useStep();

  const tzOptions = TIMEZONES.some((t) => t.value === timezone) ? TIMEZONES : [{ value: timezone, label: timezone }, ...TIMEZONES];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 2) {
      setNameError('Enter your firm name.');
      return;
    }
    setNameError(null);
    void run(async () => {
      await post('firm', { name: name.trim(), phone: phone.trim() || null, address: address.trim() || null, timezone, region: region || null });
      onDone();
    });
  };

  return (
    <form onSubmit={submit} noValidate>
      <StepHeader title="Tell us about your firm" description="This appears on your intake forms, booking pages and client emails." />
      <div className="space-y-5">
        <Field label="Firm name" htmlFor="ob-name" required error={nameError}>
          <Input id="ob-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="organization" aria-invalid={!!nameError || undefined} />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Phone" htmlFor="ob-phone">
            <Input id="ob-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
          </Field>
          <Field label="Province or territory" htmlFor="ob-region">
            <Select id="ob-region" value={region} onChange={(e) => setRegion(e.target.value)} options={PROVINCES} placeholder="Select" />
          </Field>
        </div>
        <Field label="Office address" htmlFor="ob-address">
          <Textarea id="ob-address" rows={2} value={address} onChange={(e) => setAddress(e.target.value)} autoComplete="street-address" />
        </Field>
        <Field label="Time zone" htmlFor="ob-tz" description="Bookings, deadlines and reminders use this time zone.">
          <Select id="ob-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)} options={tzOptions} />
        </Field>
        <FormError message={error} />
      </div>
      <StepActions loading={loading} />
    </form>
  );
}

function PracticeAreasStep({ me, onDone, onBack }: { me: Me; onDone: () => void; onBack: () => void }) {
  const [selected, setSelected] = useState<string[]>(me.settings.practiceAreas);
  const { loading, error, setError, run } = useStep();
  const toggle = (a: string) => setSelected((s) => (s.includes(a) ? s.filter((x) => x !== a) : [...s, a]));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (selected.length === 0) {
      setError('Choose at least one practice area, or skip this step.');
      return;
    }
    void run(async () => {
      await post('practice_areas', { areas: selected });
      onDone();
    });
  };

  return (
    <form onSubmit={submit} noValidate>
      <StepHeader
        title="What kinds of matters do you handle?"
        description="For each area we create a matter type with sensible stages and an intake form preset. You can edit them any time in Settings."
      />
      <fieldset>
        <legend className="sr-only">Practice areas</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {intake.PRACTICE_AREAS.map((a) => {
            const on = selected.includes(a);
            return (
              <button
                key={a}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => toggle(a)}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-xl px-4 py-3.5 text-left text-sm font-medium transition-[box-shadow,background-color] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30',
                  on ? 'bg-accent shadow-[inset_0_0_0_2px_var(--color-primary)]' : 'bg-card shadow-[inset_0_0_0_1px_var(--color-border)] hover:bg-muted',
                )}
              >
                <span>{intake.PRACTICE_AREA_LABELS[a]}</span>
                <span className={cn('flex size-5 items-center justify-center rounded-full', on ? 'bg-primary text-primary-foreground' : 'shadow-[inset_0_0_0_1px_var(--color-input)]')}>
                  {on ? <Check className="size-3.5" aria-hidden /> : null}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>
      <div className="mt-5">
        <FormError message={error} />
      </div>
      <StepActions onBack={onBack} onSkip={onDone} loading={loading} />
    </form>
  );
}

function AvailabilityStep({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const [rules, setRules] = useState<AvailabilityRule[]>(DEFAULT_RULES);
  const { loading, error, setError, run } = useStep();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const problems = booking.validateRules(rules);
    if (problems.length) {
      setError(problems[0] ?? 'Check your hours.');
      return;
    }
    void run(async () => {
      await post('availability', { rules });
      onDone();
    });
  };

  return (
    <form onSubmit={submit} noValidate>
      <StepHeader title="When can clients book consultations?" description="Your weekly hours for online booking. Clients only see open slots inside these hours." />
      <AvailabilityEditor rules={rules} onChange={setRules} multiple={false} />
      <div className="mt-5">
        <FormError message={error} />
      </div>
      <StepActions onBack={onBack} onSkip={onDone} loading={loading} />
    </form>
  );
}

function PaymentsStep({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const [trustLabel, setTrustLabel] = useState('Trust account');
  const [trustId, setTrustId] = useState('');
  const [opLabel, setOpLabel] = useState('Operating account');
  const [opId, setOpId] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const { loading, error, run } = useStep();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const accounts: payments.PaymentAccount[] = [
      { destination: 'trust', label: trustLabel.trim() || 'Trust account', stripeAccountId: trustId.trim() || null },
      { destination: 'operating', label: opLabel.trim() || 'Operating account', stripeAccountId: opId.trim() || null },
    ];
    const found = payments.validateAccounts(accounts);
    setProblems(found);
    if (found.length) return;
    void run(async () => {
      await post('payments', {
        trust: { label: accounts[0]!.label, stripeAccountId: accounts[0]!.stripeAccountId },
        operating: { label: accounts[1]!.label, stripeAccountId: accounts[1]!.stripeAccountId },
      });
      onDone();
    });
  };

  return (
    <form onSubmit={submit} noValidate>
      <StepHeader
        title="Where should payments go?"
        description="Retainers go to your trust account and earned fees to your operating account by default. Add the Stripe account for each; you can do this later."
      />
      <Alert variant="info" title="Trust accounting" className="mb-6">
        {payments.TRUST_COMPLIANCE_NOTICE}
      </Alert>
      <div className="space-y-6">
        <fieldset className="space-y-4">
          <legend className="mb-1 text-sm font-semibold">Trust account</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Label" htmlFor="ob-trust-label">
              <Input id="ob-trust-label" value={trustLabel} onChange={(e) => setTrustLabel(e.target.value)} />
            </Field>
            <Field label="Stripe account id" htmlFor="ob-trust-id" description="Optional. Looks like acct_...">
              <Input id="ob-trust-id" className="figures" placeholder="acct_..." value={trustId} onChange={(e) => setTrustId(e.target.value)} autoComplete="off" />
            </Field>
          </div>
        </fieldset>
        <fieldset className="space-y-4">
          <legend className="mb-1 text-sm font-semibold">Operating account</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Label" htmlFor="ob-op-label">
              <Input id="ob-op-label" value={opLabel} onChange={(e) => setOpLabel(e.target.value)} />
            </Field>
            <Field label="Stripe account id" htmlFor="ob-op-id" description="Optional. Looks like acct_...">
              <Input id="ob-op-id" className="figures" placeholder="acct_..." value={opId} onChange={(e) => setOpId(e.target.value)} autoComplete="off" />
            </Field>
          </div>
        </fieldset>
        {problems.length ? (
          <Alert variant="destructive" title="Check your accounts">
            <ul className="list-disc space-y-1 pl-5">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        <FormError message={error} />
      </div>
      <StepActions onBack={onBack} onSkip={onDone} loading={loading} />
    </form>
  );
}

type InviteRow = { email: string; role: Role };

function TeamStep({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const [rows, setRows] = useState<InviteRow[]>([{ email: '', role: 'lawyer' }]);
  const [sent, setSent] = useState<Invitation[]>([]);
  const [limitError, setLimitError] = useState<string | null>(null);
  const { loading, error, setError, run } = useStep();
  const roleOptions = workspace.ROLES.map((r) => ({ value: r, label: workspace.ROLE_LABELS[r] }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const invites = rows.map((r) => ({ email: r.email.trim(), role: r.role })).filter((r) => r.email);
    if (invites.length === 0) {
      if (sent.length) onDone();
      else setError('Add at least one email address, or skip this step.');
      return;
    }
    const bad = invites.find((r) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email));
    if (bad) {
      setError(`${bad.email} is not a valid email address.`);
      return;
    }
    setLimitError(null);
    void run(async () => {
      try {
        const res = await post('team', { invites });
        setSent((s) => [...s, ...(res.invitations ?? [])]);
        setRows([{ email: '', role: 'lawyer' }]);
      } catch (err) {
        if (isApiError(err, 'limit_reached')) {
          setLimitError(err.message);
          return;
        }
        throw err;
      }
    });
  };

  return (
    <form onSubmit={submit} noValidate>
      <StepHeader title="Invite your team" description="Each person gets an email with a link to join. You can also copy the link and send it yourself." />
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <Field label={i === 0 ? 'Email' : undefined} htmlFor={`ob-inv-email-${i}`} className="flex-1">
              <Input
                id={`ob-inv-email-${i}`}
                type="email"
                aria-label={`Invite ${i + 1} email`}
                placeholder="name@yourfirm.ca"
                value={r.email}
                onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
              />
            </Field>
            <div className="flex gap-2">
              <Field label={i === 0 ? 'Role' : undefined} htmlFor={`ob-inv-role-${i}`} className="flex-1 sm:w-36 sm:flex-none">
                <Select
                  id={`ob-inv-role-${i}`}
                  aria-label={`Invite ${i + 1} role`}
                  value={r.role}
                  options={roleOptions}
                  onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, role: e.target.value as Role } : x)))}
                />
              </Field>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove invite ${i + 1}`}
                disabled={rows.length === 1}
                onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
              >
                <Trash2 />
              </Button>
            </div>
          </div>
        ))}
        <Button type="button" variant="link" size="sm" onClick={() => setRows((rs) => [...rs, { email: '', role: 'lawyer' }])} disabled={rows.length >= 20}>
          <Plus /> Add another
        </Button>
      </div>
      {limitError ? (
        <Alert variant="warning" title="Seat limit reached" className="mt-5">
          {limitError} You can upgrade after setup in Settings, Billing.
        </Alert>
      ) : null}
      <div className="mt-5">
        <FormError message={error} />
      </div>
      {sent.length ? (
        <div className="mt-6 space-y-2">
          <h2 className="text-sm font-semibold">Invitations sent</h2>
          <ul className="divide-y divide-border/70 rounded-xl border border-border/70">
            {sent.map((inv) => (
              <li key={inv.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{inv.email}</p>
                  <p className="text-xs text-muted-foreground">{workspace.ROLE_LABELS[inv.role] ?? inv.role}</p>
                </div>
                <CopyButton value={inv.link} label="Copy link" copiedLabel="Invitation link copied" />
              </li>
            ))}
          </ul>
          <div className="flex justify-end pt-2">
            <Button type="button" variant="outline" onClick={onDone}>
              Continue without more invites
            </Button>
          </div>
        </div>
      ) : null}
      <StepActions onBack={onBack} onSkip={onDone} loading={loading} submitLabel="Send invitations" />
    </form>
  );
}

function CompleteStep({ me, onFinish, onBack }: { me: Me; onFinish: () => Promise<void>; onBack: () => void }) {
  const { loading, error, run } = useStep();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void run(onFinish);
      }}
    >
      <div className="flex flex-col items-center gap-4 py-4 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-success/12 text-success">
          <Check className="size-6" aria-hidden />
        </div>
        <div className="space-y-1.5">
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.025em]">{me.workspace.name} is ready</h1>
          <p className="mx-auto max-w-md text-[15px] text-muted-foreground">
            Share your intake form and booking link, then watch new enquiries arrive on your dashboard. Anything you skipped is waiting in Settings.
          </p>
        </div>
      </div>
      <FormError message={error} />
      <StepActions onBack={onBack} loading={loading} submitLabel="Go to dashboard" />
    </form>
  );
}
